import {callbackCancelledResult, useSafeAsyncCallback} from '@github-ui/use-safe-async-callback'
import {ClockIcon} from '@primer/octicons-react'
import {useCallback} from 'react'
import invariant from 'tiny-invariant'

import type {
  ItemUpdates,
  ItemUpdatesWithSourceRepoId,
  UpdateColumnValueAction,
} from '../../api/columns/contracts/storage'
import {cancelGetAllMemexData} from '../../api/memex/api-get-all-memex-data'
import {apiBulkUpdateItems} from '../../api/memex-items/api-bulk-update-items'
import {BulkColumnValueUpdate} from '../../api/stats/contracts'
import useToasts, {ToastType} from '../../components/toasts/use-toasts'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useBulkUpdateItemsContext} from '../../hooks/use-bulk-update-items-context'
import {UpdateValidationError, validateUpdate} from '../../hooks/use-update-item'
import {BulkUpdateResources, Resources} from '../../strings'
import {useFindLoadedFieldIdsForCurrentView} from '../columns/use-find-loaded-field-ids'
import {usePaginatedMemexItemsQueryContext} from '../memex-items/queries/paginated-memex-items-query-context'
import {useFindMemexItem} from '../memex-items/use-find-memex-item'
import {useSetMemexItemData} from '../memex-items/use-set-memex-item-data'
import {BulkUpdateProgressSingleton} from './bulk-update-progress'
import {buildRevertForUpdate} from './column-value'
import {mapToLocalUpdate, mapToRemoteUpdate} from './column-value-payload'
import {useSetColumnValue} from './use-set-column-value'

export const MAX_BULK_ITEMS = 50

export const useBulkUpdateItemColumnValues = () => {
  const bulkUpdateContext = useBulkUpdateItemsContext()
  const {addPersistedToast} = useToasts()
  const {setColumnValue} = useSetColumnValue()
  const {setItemData} = useSetMemexItemData()
  const {findMemexItem} = useFindMemexItem()
  const {postStats} = usePostStats()
  const {findLoadedFieldIdsForCurrentView} = useFindLoadedFieldIdsForCurrentView()
  const loadedFieldIds = findLoadedFieldIdsForCurrentView()
  const paginatedItemsQueryContext = usePaginatedMemexItemsQueryContext()

  /**
   * Perform individual per-item updates and return an array of revert updates for undoing. Works with IDs so that the
   * updates are always performed on the latest instances of the items. Wrapped in `useSafeAsyncCallback` for safety
   * when undoing.
   */
  const performUpdates = useSafeAsyncCallback(
    async (allItemUpdates: Array<ItemUpdatesWithSourceRepoId>, skipInvalidItems = false) => {
      const itemUpdates = []
      const reverts: Array<ItemUpdates> = []

      let skippedItemCount = 0
      for (const {itemId, updates, sourceRepoId} of allItemUpdates) {
        const item = findMemexItem(itemId)
        if (!item) continue

        const itemReverts = []
        const remoteUpdates = []
        let hasSkippedUpdates = false

        for (const update of updates) {
          const remoteUpdate = mapToRemoteUpdate(update)

          if (skipInvalidItems) {
            try {
              validateUpdate([item], update, sourceRepoId ?? null)
            } catch {
              hasSkippedUpdates = true
              continue
            }
          }

          const revert = buildRevertForUpdate(update, item)

          // Optimistically update the values locally
          const localUpdate = mapToLocalUpdate(update)

          itemReverts.push(revert)
          setColumnValue(item, localUpdate)

          if (!remoteUpdate) continue
          remoteUpdates.push(remoteUpdate)
        }

        if (hasSkippedUpdates) skippedItemCount += 1

        reverts.push({
          itemId,
          updates: itemReverts,
        })

        if (!remoteUpdates.length) continue // Non-updatable column

        itemUpdates.push({
          id: item.id,
          memexProjectColumnValues: remoteUpdates,
        })
      }

      try {
        cancelGetAllMemexData()

        if (skipInvalidItems && itemUpdates.length === 0) {
          throw new UpdateValidationError(Resources.noValidUpdates)
        }

        const firstUpdate = itemUpdates[0]
        invariant(firstUpdate, 'need at least one project item update')

        const firstColumnValue = firstUpdate.memexProjectColumnValues[0]
        invariant(firstColumnValue, 'need at least one column value')

        const memexProjectColumnId = firstColumnValue.memexProjectColumnId

        bulkUpdateContext?.onPreparingToBulkUpdate(
          reverts,
          itemUpdates.map(itemUpdate => itemUpdate.id),
          memexProjectColumnId,
        )

        postStats({
          name: BulkColumnValueUpdate,
          ui: 'shortcut',
          context: itemUpdates.length,
          memexProjectColumnId,
        })

        const bulkUpdateResponse = await apiBulkUpdateItems({
          memexProjectItems: itemUpdates,
          fieldIds: loadedFieldIds,
        })

        const showBulkUpdateToast =
          itemUpdates.length > 1 &&
          bulkUpdateResponse.job &&
          BulkUpdateProgressSingleton.pending(bulkUpdateResponse.requestId, skippedItemCount)

        if (showBulkUpdateToast) {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addPersistedToast({
            message: BulkUpdateResources.startMessage,
            icon: ClockIcon,
            type: ToastType.default,
          })
        }

        if ('memexProjectItems' in bulkUpdateResponse && bulkUpdateResponse.memexProjectItems) {
          for (const item of bulkUpdateResponse.memexProjectItems) {
            setItemData(item)
          }
          if (bulkUpdateResponse.invalidateQueryCache) {
            paginatedItemsQueryContext?.handlePaginatedDataRefresh()
          }
        }

        if (bulkUpdateContext && 'bulkUpdateErrors' in bulkUpdateResponse && bulkUpdateResponse.bulkUpdateErrors) {
          bulkUpdateContext.setErrors(bulkUpdateResponse.bulkUpdateErrors)
        }

        return reverts
      } catch (error) {
        for (const {itemId, updates} of reverts) {
          const item = findMemexItem(itemId)
          if (item) {
            for (const update of updates) {
              setColumnValue(item, mapToLocalUpdate(update))
            }
          }
        }

        throw error
      }
    },
  )

  /** Apply the same update to many columns. Returns a function that can undo the changes. */
  const bulkUpdateColumnValues = useCallback(
    async (
      itemUpdates: Array<{
        itemId: number
        updates: Array<UpdateColumnValueAction>
      }>,
      skipInvalidUpdates = false,
    ) => {
      const reverts = await performUpdates(itemUpdates, skipInvalidUpdates)

      return async () => {
        // It's crucial that this is a safe call to avoid calling an outdated reference if the user clicks much later.
        if (reverts !== callbackCancelledResult) await performUpdates(reverts)
      }
    },
    [performUpdates],
  )

  return {bulkUpdateColumnValues}
}
